import { ChangeDetectorRef, Component, forwardRef, inject, input, model } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cn } from './cn';

@Component({
  selector: 'hlm-switch',
  standalone: true,
  // Explicit inline-flex host: a custom-element host defaults to display:inline, which as a flex
  // item gets blockified with line-box leading — pushing the visible toggle+label to the top of a
  // taller box so it sits misaligned above sibling controls. inline-flex tightly wraps + centers it.
  host: { class: 'inline-flex items-center' },
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HlmSwitchComponent), multi: true },
  ],
  template: `
    <label [class]="_rootClass()">
      <button
        type="button"
        role="switch"
        [attr.aria-checked]="checked()"
        [attr.aria-label]="ariaLabel() ?? label()"
        [disabled]="disabled()"
        (click)="_toggle()"
        (blur)="onTouched()"
        [class]="_trackClass()"
      >
        <span [class]="_thumbClass()"></span>
      </button>
      @if (label()) {
        <span class="text-sm text-foreground">{{ label() }}</span>
      }
      <ng-content />
    </label>
  `,
})
export class HlmSwitchComponent implements ControlValueAccessor {
  readonly checked = model(false);
  readonly disabled = model(false);
  readonly label = input<string | null>(null);
  /**
   * Accessible name, when it must differ from the visible label.
   *
   * A label that changes with the state ("Must have" / "Nice to have") is fine to read
   * but wrong to announce: the control would say "Nice to have, switch, not checked" — a
   * name that negates itself against `aria-checked` (PR #42 review, roundup #14). Give it
   * a static name and let the visible text carry the state.
   */
  readonly ariaLabel = input<string | null>(null);
  readonly userClass = input<string>('', { alias: 'class' });

  private cdr = inject(ChangeDetectorRef);
  private onChange: (v: boolean) => void = () => {};
  onTouched: () => void = () => {};

  protected _rootClass = () =>
    cn(
      'inline-flex items-center gap-2.5 cursor-pointer select-none',
      this.disabled() && 'opacity-60 cursor-not-allowed',
      this.userClass(),
    );

  protected _trackClass = () =>
    cn(
      'relative inline-flex h-5 w-9 flex-none items-center rounded-full transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:ring-offset-2',
      this.checked() ? 'bg-primary' : 'bg-muted',
    );

  protected _thumbClass = () =>
    cn(
      'absolute h-4 w-4 rounded-full bg-card shadow-sm transition-transform',
      this.checked() ? 'translate-x-[18px]' : 'translate-x-0.5',
    );

  _toggle(): void {
    if (this.disabled()) return;
    const v = !this.checked();
    this.checked.set(v);
    this.onChange(v);
    this.cdr.markForCheck();
  }

  writeValue(v: boolean): void { this.checked.set(!!v); this.cdr.markForCheck(); }
  registerOnChange(fn: (v: boolean) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled.set(d); }
}
